import React, { useState, useCallback } from 'react';
import StageModale from './StageModale';

/**
 * SUIVI DE STAGE — exclure un dossier du suivi actif (lot 3.3)
 * =============================================================
 * Spec     : doc/modules/suivi_de_stage.md § « Exclure du suivi », écart E17
 * Maquette : doc/maquettes/suivi_de_stage_maquette.html (#suivi-modal-overlay)
 *
 * ── ⚠️ CE GESTE SORT DU MODULE ─────────────────────────────────────────────
 * C'est le seul écran du Suivi de stage qui écrit dans le dossier **scolaire**
 * de l'étudiant. Un abandon n'est pas un fait de stage : il change son
 * inscription de l'année, et — si c'est l'année active — la colonne que le
 * Planning et l'EDT lisent encore.
 *
 * L'écran doit donc **dire ce qu'il fait ailleurs**. Une exclusion silencieuse
 * qui modifie la scolarité serait la pire des surprises : celle qu'on découvre
 * dans un autre module, des semaines plus tard, sans savoir d'où elle vient.
 *
 * ── LES TROIS ÉTATS, ET POURQUOI « ACTIF » EN FAIT PARTIE ──────────────────
 * La maquette dessine trois boutons radio encadrés, dont **« Suivi actif »**.
 * Ce n'est pas un remplissage : c'est par LUI qu'on réintègre un dossier exclu.
 * Une fenêtre « Exclure » sans chemin de retour obligerait à en inventer un
 * second, ailleurs — et l'assistante chercherait.
 */

const OPTIONS = [
    {
        cle: 'actif', couleur: 'vert', icone: 'fa-user-check', titre: 'Suivi actif',
        texte: "L'étudiant est suivi normalement par l'application."
    },
    {
        cle: 'abandon_scolaire', couleur: 'rouge', icone: 'fa-user-slash', titre: 'Abandon de scolarité',
        texte: "L'étudiant a quitté la formation — le dossier sort du suivi.",
        // ⚠️ La conséquence hors module, annoncée AVANT le clic.
        ailleurs: "Son inscription de l'année passera en « abandon », et le module Étudiants le verra."
    },
    {
        cle: 'stage_etranger', couleur: 'bleu', icone: 'fa-plane', titre: "Stage à l'étranger",
        texte: 'Stage géré hors application — le dossier sort du tableau.',
        ailleurs: "Son inscription passera en « mobilité ». Sa scolarité n'est PAS touchée : il reste étudiant à part entière."
    },
    {
        // v75 (2026-10-10) : l'étudiant est passé en apprentissage / FC après la création de son dossier.
        cle: 'passage_alternance', couleur: 'violet', icone: 'fa-arrows-rotate', titre: 'Passé en alternance',
        texte: "L'étudiant est désormais apprenti ou en formation continue : il est suivi dans l'onglet Alternance — le dossier sort du suivi des stages.",
        ailleurs: "Son inscription n'est PAS touchée : il reste dans le registre Alternance et dans la transmission au CFA."
    }
];
/** Le libellé d'un motif d'exclusion (tableau de bord, panneau du dossier). */
export const LIBELLE_MOTIF = { abandon_scolaire: 'Abandon de scolarité', stage_etranger: "Stage à l'étranger", passage_alternance: 'Passé en alternance' };

function StageModaleExclusion({ dossier, enCours, onFermer, onExclure, onReintegrer }) {
    const dejaExclu = !!dossier.motif_exclusion;
    const [choix, setChoix] = useState(dossier.motif_exclusion || 'actif');
    const [commentaire, setCommentaire] = useState(dossier.commentaire_exclusion || '');

    const option = OPTIONS.find((o) => o.cle === choix);
    // ⚠️ Rien à faire si l'état choisi est déjà celui du dossier : le bouton
    // reste désactivé plutôt que d'écrire une entrée d'historique pour rien.
    const changement = choix !== (dossier.motif_exclusion || 'actif');

    const valider = useCallback(() => {
        if (choix === 'actif') onReintegrer();
        else onExclure(choix, commentaire.trim() || null);
    }, [choix, commentaire, onExclure, onReintegrer]);

    return (
        <StageModale
            titre={`Suivi de ${dossier.etudiant?.prenom} ${dossier.etudiant?.nom}`}
            icone="fa-user-gear"
            ton={choix === 'abandon_scolaire' ? 'danger' : 'neutre'}
            enCours={enCours}
            libelleValider={choix === 'actif' ? 'Réintégrer au suivi' : 'Confirmer'}
            onValider={changement ? valider : null}
            libelleAnnuler="Annuler"
            onFermer={onFermer}
        >
            <div className="stg-exclusion-options">
                {OPTIONS.map((o) => (
                    <label key={o.cle}
                           className={'stg-exclusion-option'
                               + (choix === o.cle ? ' stg-exclusion-option-' + o.couleur : '')}>
                        <input type="radio" name="suivi_statut" value={o.cle}
                               checked={choix === o.cle} disabled={enCours}
                               onChange={() => setChoix(o.cle)} />
                        <span>
                            <span className={'stg-exclusion-titre stg-exclusion-titre-' + o.couleur}>
                                <i className={'fa-solid ' + o.icone} /> {o.titre}
                            </span>
                            <span className="stg-exclusion-texte">{o.texte}</span>
                        </span>
                    </label>
                ))}
            </div>

            {/* ⚠️ CE QUI SE PASSE AILLEURS, annoncé AVANT le clic — jamais après.
                C'est le seul écran du module qui touche la scolarité. */}
            {option?.ailleurs && changement && (
                <div className="stg-encart stg-encart-alerte" style={{ marginTop: 12 }}>
                    <i className="fa-solid fa-arrow-right-arrow-left" />
                    <span>{option.ailleurs}</span>
                </div>
            )}

            {/* ⚠️ La réintégration ne remet PAS la scolarité, et l'écran le dit.
                Reprendre un dossier de stage ne prouve pas qu'un étudiant a repris
                ses études : cette décision-là appartient au module Étudiants, à
                quelqu'un qui la connaît. */}
            {choix === 'actif' && dejaExclu && (
                <div className="stg-encart stg-encart-info" style={{ marginTop: 12 }}>
                    <i className="fa-solid fa-circle-info" />
                    <span>
                        Le dossier repart en <b>« Envoyer le lien »</b>. ⚠️ Si l'étudiant avait été
                        déclaré en abandon, <b>sa scolarité n'est pas rétablie automatiquement</b> —
                        cela se fait dans le module <b>Étudiants</b>.
                    </span>
                </div>
            )}

            {choix !== 'actif' && (
                <div className="form-group" style={{ marginTop: 12, marginBottom: 0 }}>
                    <label htmlFor="excl-com">
                        Commentaire <span style={{ fontWeight: 400, color: '#64748b' }}>(facultatif)</span>
                    </label>
                    <textarea id="excl-com" className="input-sm" rows={2} value={commentaire}
                              disabled={enCours} onChange={(e) => setCommentaire(e.target.value)}
                              placeholder="Ex : abandon confirmé le 15/05/2026…" />
                </div>
            )}
        </StageModale>
    );
}

export default StageModaleExclusion;
